import { styled } from '@openeducation/oe-it-themes';
import React, { useCallback, useRef, useState } from 'react';
import {
  FlatList,
  ListRenderItem,
  StyleProp,
  View,
  ViewStyle,
  ViewToken,
} from 'react-native';

import { ArrowButton } from '../../atoms/arrow-button/arrow-button';
import { flex } from '../../style-utils';

export type CarouselBaseProps<T> = {
  data: T[];
  itemWidth: number;
  renderItem: ListRenderItem<T>;
  style?: StyleProp<ViewStyle>;
  displayArrows?: boolean;
};

export const CarouselBase = <T extends unknown>({
  data,
  style,
  renderItem,
  itemWidth,
  displayArrows = true,
}: CarouselBaseProps<T>) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [carouselWidth, setCarouselWidth] = useState(0);
  const flatListRef = useRef<FlatList>(null);
  const visibleItems = Math.floor(carouselWidth / itemWidth);
  const showArrows = displayArrows && data.length * itemWidth > carouselWidth;

  const scrollToIndex = useCallback(
    (index: number) => () => {
      flatListRef?.current?.scrollToIndex?.({ index });
    },
    [flatListRef]
  );

  const keyExtractor = useCallback((_: T, i: number) => `${i}`, []);

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) =>
      setCurrentIndex(viewableItems[0]?.index || 0),
    [setCurrentIndex]
  );

  return (
    <View style={[flex('flex-row', 'align-items-center'), style]}>
      {showArrows && (
        <ArrowButtonLeft
          onPress={scrollToIndex(currentIndex - 1)}
          disabled={currentIndex === 0}
        />
      )}
      <FlatList
        ref={flatListRef}
        onLayout={(e) => setCarouselWidth(e.nativeEvent.layout.width)}
        data={data}
        renderItem={renderItem}
        horizontal
        showsHorizontalScrollIndicator={false}
        keyExtractor={keyExtractor}
        bounces={false}
        decelerationRate={0}
        renderToHardwareTextureAndroid
        snapToInterval={itemWidth}
        snapToAlignment="start"
        scrollEventThrottle={16}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={{ itemVisiblePercentThreshold: 50 }}
      />
      {showArrows && (
        <ArrowButtonRight
          onPress={scrollToIndex(currentIndex + 1)}
          disabled={currentIndex >= data.length - visibleItems}
        />
      )}
    </View>
  );
};

const ArrowButtonLeft = styled(ArrowButton).attrs({
  direction: 'left',
  arrowStyle: {
    marginRight: 11,
    paddingRight: 5,
  },
})``;

const ArrowButtonRight = styled(ArrowButton).attrs({
  direction: 'right',
  arrowStyle: {
    marginLeft: 11,
    paddingLeft: 5,
  },
})``;

export default CarouselBase;
